/**
 * Change password component for authenticated users
 * Allows users to update their password with validation
 *
 * @module ChangePassword
 */

import { useState } from 'react'
import { logger } from '../../utils/logger'
import { useAuth } from '../../hooks/useAuth'
import { validatePassword } from '../../utils/helpers'
import { PASSWORD_REQUIREMENTS } from '../../utils/constants'
import type { ChangePasswordData } from '../../types'

/**
 * Props for ChangePassword component
 */
interface ChangePasswordProps {
  onSuccess?: () => void
  onCancel?: () => void
}

/**
 * Change password component
 * Renders form for user to change their current password
 */
export function ChangePassword({ onSuccess, onCancel }: ChangePasswordProps) {
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [newPasswordConfirm, setNewPasswordConfirm] = useState('')
  const [currentPasswordError, setCurrentPasswordError] = useState('')
  const [newPasswordError, setNewPasswordError] = useState('')
  const [confirmPasswordError, setConfirmPasswordError] = useState('')
  const [formError, setFormError] = useState('')
  const [successMessage, setSuccessMessage] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const auth = useAuth()

  logger.debug(`ChangePassword: component mounted`)

  /**
   * Validate current password
   */
  const validateCurrentPassword = (value: string): boolean => {
    logger.debug(`ChangePassword.validateCurrentPassword: validating (length=${value.length})`)

    if (!value) {
      setCurrentPasswordError('Current password is required')
      return false
    }

    setCurrentPasswordError('')
    return true
  }

  /**
   * Validate new password meets requirements
   */
  const validateNewPasswordInput = (value: string): boolean => {
    logger.debug(`ChangePassword.validateNewPasswordInput: validating (length=${value.length})`)

    const validation = validatePassword(value)

    if (!validation.valid) {
      setNewPasswordError(validation.errors.join('\n'))
      logger.debug(`ChangePassword.validateNewPasswordInput: invalid`, { errors: validation.errors })
      return false
    }

    setNewPasswordError('')
    return true
  }

  /**
   * Validate password confirmation
   */
  const validateConfirmPassword = (value: string): boolean => {
    logger.debug(`ChangePassword.validateConfirmPassword: validating`)

    if (value !== newPassword) {
      setConfirmPasswordError('Passwords do not match')
      logger.debug(`ChangePassword.validateConfirmPassword: passwords do not match`)
      return false
    }

    setConfirmPasswordError('')
    return true
  }

  /**
   * Handle current password input change
   */
  const handleCurrentPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    logger.debug(`ChangePassword.handleCurrentPasswordChange: value changed (length=${value.length})`)
    setCurrentPassword(value)
    setFormError('')

    if (currentPasswordError) {
      validateCurrentPassword(value)
    }
  }

  /**
   * Handle new password input change
   */
  const handleNewPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    logger.debug(`ChangePassword.handleNewPasswordChange: value changed (length=${value.length})`)
    setNewPassword(value)
    setFormError('')

    if (newPasswordError) {
      validateNewPasswordInput(value)
    }

    if (newPasswordConfirm && confirmPasswordError) {
      validateConfirmPassword(newPasswordConfirm)
    }
  }

  /**
   * Handle confirmation password input change
   */
  const handleConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    logger.debug(`ChangePassword.handleConfirmPasswordChange: value changed (length=${value.length})`)
    setNewPasswordConfirm(value)
    setFormError('')

    if (confirmPasswordError) {
      validateConfirmPassword(value)
    }
  }

  /**
   * Handle form submission
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    logger.debug(`ChangePassword.handleSubmit: submitting form`)

    // Validate all inputs
    const currentValid = validateCurrentPassword(currentPassword)
    const newValid = validateNewPasswordInput(newPassword)
    const confirmValid = validateConfirmPassword(newPasswordConfirm)

    if (!currentValid || !newValid || !confirmValid) {
      logger.debug(`ChangePassword.handleSubmit: validation failed`)
      return
    }

    setIsSubmitting(true)
    setSuccessMessage('')

    try {
      const data: ChangePasswordData = {
        oldPassword: currentPassword,
        newPassword,
      }

      logger.debug(`ChangePassword.handleSubmit: sending password change request`)
      await auth.changePassword(data)

      logger.info(`ChangePassword.handleSubmit: password changed successfully`)
      setSuccessMessage('Password changed successfully!')

      // Reset form
      setCurrentPassword('')
      setNewPassword('')
      setNewPasswordConfirm('')

      // Call success callback after a brief delay
      setTimeout(() => {
        if (onSuccess) {
          onSuccess()
        }
      }, 1500)
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error)
      logger.error(`ChangePassword.handleSubmit: password change failed`, { error: errorMessage })
      setFormError(errorMessage || 'Failed to change password. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  logger.debug(`ChangePassword: rendering`, {
    hasCurrentPassword: currentPassword.length > 0,
    hasNewPassword: newPassword.length > 0,
    isSubmitting,
    hasErrors: Boolean(formError),
  })

  return (
    <form onSubmit={handleSubmit} className="space-y-4 w-full max-w-sm">
      <div className="bg-walrus-white border border-walrus-gray rounded-lg p-6 space-y-4">
        <h2 className="text-xl font-display text-walrus-black mb-4">Change Password</h2>

        {formError && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
            <p className="text-sm">{formError}</p>
          </div>
        )}

        {successMessage && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded">
            <p className="text-sm">{successMessage}</p>
          </div>
        )}

        <div>
          <label className="block text-walrus-black text-sm font-semibold mb-2">Current Password</label>
          <input
            type="password"
            value={currentPassword}
            onChange={handleCurrentPasswordChange}
            onBlur={() => validateCurrentPassword(currentPassword)}
            className={`w-full px-4 py-2 bg-walrus-white border rounded text-walrus-black placeholder-walrus-silver focus:outline-none focus:ring-1 transition-colors ${
              currentPasswordError
                ? 'border-red-400 focus:border-red-400 focus:ring-red-400'
                : 'border-walrus-gray focus:border-walrus-black focus:ring-walrus-black'
            }`}
            placeholder="••••••••"
            disabled={isSubmitting}
          />
          {currentPasswordError && <p className="text-red-600 text-xs mt-1">{currentPasswordError}</p>}
        </div>

        <div className="border-t border-walrus-gray pt-4">
          <h3 className="text-walrus-black text-sm font-semibold mb-3">New Password</h3>

          <div className="mb-3">
            <label className="block text-walrus-black text-sm mb-2">Password</label>
            <input
              type="password"
              value={newPassword}
              onChange={handleNewPasswordChange}
              onBlur={() => validateNewPasswordInput(newPassword)}
              className={`w-full px-4 py-2 bg-walrus-white border rounded text-walrus-black placeholder-walrus-silver focus:outline-none focus:ring-1 transition-colors ${
                newPasswordError
                  ? 'border-red-400 focus:border-red-400 focus:ring-red-400'
                  : 'border-walrus-gray focus:border-walrus-black focus:ring-walrus-black'
              }`}
              placeholder="••••••••"
              disabled={isSubmitting}
            />
            {newPasswordError && <p className="text-red-600 text-xs mt-1 whitespace-pre-line">{newPasswordError}</p>}
          </div>

          <div className="mb-3">
            <label className="block text-walrus-black text-sm mb-2">Confirm Password</label>
            <input
              type="password"
              value={newPasswordConfirm}
              onChange={handleConfirmPasswordChange}
              onBlur={() => validateConfirmPassword(newPasswordConfirm)}
              className={`w-full px-4 py-2 bg-walrus-white border rounded text-walrus-black placeholder-walrus-silver focus:outline-none focus:ring-1 transition-colors ${
                confirmPasswordError
                  ? 'border-red-400 focus:border-red-400 focus:ring-red-400'
                  : 'border-walrus-gray focus:border-walrus-black focus:ring-walrus-black'
              }`}
              placeholder="••••••••"
              disabled={isSubmitting}
            />
            {confirmPasswordError && <p className="text-red-600 text-xs mt-1">{confirmPasswordError}</p>}
          </div>

          <div className="bg-walrus-cream rounded p-3 space-y-1 text-walrus-silver text-xs">
            <p className="font-semibold text-walrus-black">Password must contain:</p>
            <ul className="list-disc list-inside space-y-1">
              <li className={newPassword.length >= PASSWORD_REQUIREMENTS.minLength ? 'text-green-600' : ''}>
                At least {PASSWORD_REQUIREMENTS.minLength} characters
              </li>
              <li className={/[A-Z]/.test(newPassword) ? 'text-green-600' : ''}>Uppercase letters (A-Z)</li>
              <li className={/[a-z]/.test(newPassword) ? 'text-green-600' : ''}>Lowercase letters (a-z)</li>
              <li className={/[0-9]/.test(newPassword) ? 'text-green-600' : ''}>Numbers (0-9)</li>
              <li className={/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(newPassword) ? 'text-green-600' : ''}>
                Symbols (!@#$%...)
              </li>
            </ul>
          </div>
        </div>

        <div className="flex gap-2 pt-4">
          <button
            type="submit"
            disabled={isSubmitting || !currentPassword || !newPassword || !newPasswordConfirm}
            className="flex-1 px-4 py-2 bg-walrus-black text-walrus-white font-semibold rounded hover:bg-walrus-charcoal disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isSubmitting ? 'Updating...' : 'Update Password'}
          </button>

          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              disabled={isSubmitting}
              className="px-4 py-2 bg-walrus-cream text-walrus-black font-semibold rounded hover:bg-walrus-gray disabled:opacity-50 disabled:cursor-not-allowed transition-colors border border-walrus-gray"
            >
              Cancel
            </button>
          )}
        </div>
      </div>
    </form>
  )
}
